import { HOMESCREEN_PATH, OAUTH_CALLBACK_PATH } from "../../shared/routing/paths/app";
import {
  NETWORK_SUPPLIER_FORWARD_INVITE_PATH,
  NETWORK_SUPPLIER_REGISTER_PATH,
} from "../../shared/routing/paths/network";

const INTENDED_ROUTE_KEY = "cfc_intended_route";
const PUBLIC_ROUTES = new Set([NETWORK_SUPPLIER_REGISTER_PATH, NETWORK_SUPPLIER_FORWARD_INVITE_PATH]);

/**
 * `/` is where a lost deep link lands anyway, `/oauth/callback` is a one-shot URL whose `code` is
 * spent, and public invitation routes do not initiate login. Restoring any of them would be a
 * no-op at best and would race the authenticated supplier hand-off at worst.
 */
const isRestorable = (path: string): boolean => {
  const rawPathname = path.split("?")[0];
  const pathname = rawPathname.replace(/\/+$/, "") || HOMESCREEN_PATH;

  return pathname !== HOMESCREEN_PATH && !pathname.startsWith(OAUTH_CALLBACK_PATH) && !PUBLIC_ROUTES.has(pathname);
};

/**
 * A module variable, not storage: it is armed for one document only (the one that booted on the
 * OAuth callback), so a later visit to `/` in the same tab can never replay it.
 */
let intendedRoute: string | null = null;

/**
 * Call once from the entry module, before React mounts; that timing is what makes it work.
 * app-shell's own restore (`_REDIRECT_AFTER_LOGIN_`, `authModule/helpers/useAuth`) reads and deletes
 * its key on the `isAuthenticated` edge, strictly before the route tree mounts
 * (`AuthenticationProvider` holds a spinner until `activeTenant` resolves). So `HomeEntryRoute` never
 * sees it, and whichever hop to `/` lands last wins. Reading here, off the render path, avoids that race.
 */
export const initIntendedRoute = (): void => {
  const { pathname, search } = window.location;
  const path = `${pathname}${search}`;

  if (pathname.startsWith(OAUTH_CALLBACK_PATH)) {
    const stored = sessionStorage.getItem(INTENDED_ROUTE_KEY);
    sessionStorage.removeItem(INTENDED_ROUTE_KEY);
    intendedRoute = stored !== null && isRestorable(stored) ? stored : null;

    return;
  }

  intendedRoute = null;

  if (isRestorable(path)) {
    sessionStorage.setItem(INTENDED_ROUTE_KEY, path);
  } else {
    sessionStorage.removeItem(INTENDED_ROUTE_KEY);
  }
};

/**
 * Call on every navigation. Once on a real route, the post-login intent is spent and must not fire
 * again on a later walk back to `/`; and this is the route to come back to if the session dies
 * mid-visit, which happens without a document reload, so {@link initIntendedRoute} never sees it.
 */
export const noteCurrentRoute = (path: string): void => {
  if (!isRestorable(path)) {
    // Do not clear the in-memory intent here: on the callback and its first hop to `/`, the
    // redirect provider still needs to read it. Storage is only for a later login round-trip.
    sessionStorage.removeItem(INTENDED_ROUTE_KEY);
    return;
  }

  intendedRoute = null;
  sessionStorage.setItem(INTENDED_ROUTE_KEY, path);
};

export const getIntendedRoute = (): string | null => intendedRoute;

export const consumeIntendedRoute = (): void => {
  intendedRoute = null;
};
